// BEÁGYAZOTT WYSIWYG-KÉP MEGJELENÍTÉSE — a TÁROLT, stabil út feloldása FRISS presigned URL-re.
//
// A mentett HTML-ben `<img src="/editor-images/<uuid>/content">` áll (stabil, sosem jár le). A böngésző ezt az
// utat NEM tudja közvetlenül betölteni: a BFF Bearer-tokent kér, egy `<img>` viszont nem küld `Authorization`
// fejlécet. Ezért a megjelenítés előtt EGY hitelesített hívás oldja fel presigned URL-re, és az kerül a DOM-ba.
//
// ⚠️ A presigned URL SOHA nem íródik vissza a tárolt HTML-be (lejárna, és a mentett tartalom eltörne).
import { api } from '@/lib/api';

/** A tárolt, stabil út alakja — ebből nyerjük ki a fájl-azonosítót. */
const STABLE_PATH = /^\/editor-images\/([0-9a-f-]{36})\/content$/i;

/** A presigned URL rövid életű; a cache-t ennyi idő után elengedjük (jóval a szokásos lejárat alatt). */
const TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  at: number;
  url: Promise<string | null>;
}

// Modul-szintű cache: EGY tartalomban ugyanaz a kép többször is előfordulhat, és a lista→detail navigáció is
// újra-rendereli — kép-azonosítónként egyetlen feloldó hívás elég.
const cache = new Map<string, CacheEntry>();

/** Igaz, ha az `src` a saját, tárolt utunk (a legacy `/storage/...`-ok és a külső URL-ek nem azok). */
export function isStableEditorImageSrc(src: string): boolean {
  return STABLE_PATH.test(src);
}

/**
 * A fájl-azonosító → megjelenítési URL feloldó. Alap: a BELSŐ végpont (`/editor-images/:fileId/url`).
 *
 * MIÉRT INJEKTÁLHATÓ: a PARTNER-felület ugyanezt a tárolt HTML-t rendereli, de a belső kép-végpontot nem
 * hívhatja (az belső-only, és ott bármely fájl-id feloldható lenne). A partner-lap ezért a saját, SZŰK
 * végpontját adja át (`/portal/product-updates/images/:fileId`), ami csak publikus bejegyzés törzs-képére ad
 * URL-t. A DOM-bejárás, a cache és a hiba-kezelés így EGY helyen marad.
 */
export type EditorImageResolver = (fileId: string) => Promise<string | null>;

/**
 * Egy FELÜLET-SPECIFIKUS feloldó: a hívó függvény + a cache-`scope`.
 *
 * A `scope` NEM kozmetika: a cache-kulcs része, mert ugyanarra a fájlra a belső és a partner-felület MÁS
 * URL-t kap (más token, más presign) — közös kulccsal az egyik felület a másik URL-jét kapná vissza.
 *
 * Ugyanez az alak áll a `RichTextContent`-en, a `RichTextEditor`-on és a kép-extension opcióján: egy
 * fogalom, egy típus (három strukturálisan azonos, de külön deklarált alak csendben elcsúszott volna).
 */
export interface EditorImageResolverConfig {
  resolve: EditorImageResolver;
  scope: string;
}

const internalResolver: EditorImageResolver = async (fileId) => {
  const res = await api.editorImageResolve.query({ params: { fileId } });
  return res.status === 200 ? res.body.url : null;
};

/** A cache-kulcs a fájl-id ÉS a feloldó együtt: a két felület más URL-t kap ugyanarra a fájlra. */
function cacheKey(fileId: string, scope: string): string {
  return `${scope}:${fileId}`;
}

/**
 * Egy tárolt kép-út feloldása megjelenítendő URL-re. `null`, ha nem a saját utunk, vagy a kép nem elérhető
 * (törölt fájl / jog-hiány) — a hívó ilyenkor NEM tesz `src`-t (nem villog törött kép).
 */
export async function resolveEditorImageSrc(
  src: string,
  resolver: EditorImageResolver = internalResolver,
  scope = 'internal',
): Promise<string | null> {
  const m = STABLE_PATH.exec(src);
  if (m === null) return null;
  const fileId = m[1]!;

  const key = cacheKey(fileId, scope);
  const hit = cache.get(key);
  if (hit !== undefined && Date.now() - hit.at < TTL_MS) return hit.url;

  const url = (async (): Promise<string | null> => {
    try {
      return await resolver(fileId);
    } catch {
      // Hálózati hiba: a cache-be NEM ragadhat be a kudarc → töröljük, hogy a következő render újrapróbálja.
      cache.delete(key);
      return null;
    }
  })();
  cache.set(key, { at: Date.now(), url });
  return url;
}

/**
 * Egy már DOM-ba került (sanitizált) tartalom kép-forrásainak feloldása. A `<img>`-ek a tárolt utat viselik; ez
 * cseréli őket a friss presigned URL-re. Az `AbortSignal`-lel a hívó le tudja állítani unmountnál.
 */
export function hydrateEditorImages(
  root: HTMLElement,
  signal?: AbortSignal,
  /** Elhagyható, felület-specifikus feloldó (partner-lap) — alap a belső végpont. */
  resolver?: EditorImageResolverConfig,
): void {
  // KÉP **ÉS VIDEÓ**: mindkettő a saját, stabil utat viseli, és mindkettőt ugyanaz a feloldó hidratálja (a
  // fájl-tár nem tud a MIME-típusról a presign-en). A `<video>` kihagyása „üres fekete téglalap"-ként
  // jelent volna meg — a `src`-t nélkülünk a böngésző nem tudja betölteni.
  root.querySelectorAll<HTMLImageElement | HTMLVideoElement>('img, video').forEach((img) => {
    const current = img.getAttribute('src');
    // A TÁROLT út vagy még a `src`-ben áll (első futás), vagy már a `data-stable-src`-ben (egy korábbi futás
    // levette). MINDKETTŐT figyeljük: enélkül egy MEGSZAKÍTOTT első futás után (React StrictMode: mount →
    // cleanup(abort) → mount) a kép örökre `src` nélkül maradna. A feloldás cache-elt → nincs új kérés.
    const stable =
      current !== null && isStableEditorImageSrc(current) ? current : (img.dataset.stableSrc ?? '');
    if (!isStableEditorImageSrc(stable)) return; // nem a mi képünk (a sanitizáló dolga kiszűrni)
    // Már feloldott kép (a `src` egy friss presigned URL) → nincs mit tenni.
    if (current !== null && !isStableEditorImageSrc(current)) return;

    img.dataset.stableSrc = stable;
    // A feloldásig NINCS `src` (különben a böngésző 401-es kép-kérést indítana a BFF-re).
    img.removeAttribute('src');
    void resolveEditorImageSrc(stable, resolver?.resolve, resolver?.scope).then((url) => {
      if (signal?.aborted === true || url === null) return;
      img.setAttribute('src', url);
    });
  });
}
